// client/src/coms/components/sidebar_parts/SidebarTopDrawer.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LogoFill, ServerContextMenu, formatMentionBadge, useServerNavModel } from './serverNavModel';

const MANUAL_LANDING_SESSION_KEY = 'voxemera_manual_landing_once';

function getPublicPath(p) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const path = String(p || '').startsWith('/') ? String(p || '') : `/${String(p || '')}`;
  return `${base}${path}`;
}


function requestManualVoxemeraLanding() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(MANUAL_LANDING_SESSION_KEY, '1');
  } catch {}
}

export default function SidebarTopDrawer({
  servers,
  activeServerId,
  onSelectServer,
  onCreateServer,
  onServersChanged,
  onOpenServerSettings,
  apiAvailable,
  user,
  serverUnreadById,
  serverMentionCountById,
}) {
  const [topBannerHeight, setTopBannerHeight] = useState(48);
  const [topDrawerOpen, setTopDrawerOpen] = useState(false);
  const [topDrawerHeight, setTopDrawerHeight] = useState(240);

  const rootRef = useRef(null);

  const nav = useServerNavModel({
    servers,
    activeServerId,
    onSelectServer,
    onServersChanged,
    apiAvailable,
    user,
    serverUnreadById,
    serverMentionCountById,
    rootRef,
  });
  const { closeMenu } = nav;

  const voxLogoUrl = useMemo(() => getPublicPath('/vox-favicon-32x32.png'), []);
  const pointAtlasLogoUrl = useMemo(() => getPublicPath('/favicon-32x32.png'), []);

  useEffect(() => {
    function readTopbarHeight() {
      try {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        const el = document.querySelector('.coms-topbar');
        const h = el ? Math.round(el.getBoundingClientRect().height) : 0;
        if (h > 0) setTopBannerHeight(h);
      } catch {}
    }

    readTopbarHeight();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', readTopbarHeight);
    }

    let ro = null;
    try {
      const el = typeof document !== 'undefined' ? document.querySelector('.coms-topbar') : null;
      if (el && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => readTopbarHeight());
        ro.observe(el);
      }
    } catch {}

    return () => {
      try {
        if (typeof window !== 'undefined') window.removeEventListener('resize', readTopbarHeight);
      } catch {}
      try {
        if (ro) ro.disconnect();
      } catch {}
    };
  }, []);

  useEffect(() => {
    function readDrawerHeight() {
      try {
        const rootEl = rootRef.current;
        if (!rootEl) return;
        const sidebarEl = rootEl.closest('.coms-sidebar');
        const total = Math.round((sidebarEl || rootEl).getBoundingClientRect().height);
        if (!Number.isFinite(total) || total <= 0) return;
        setTopDrawerHeight(Math.max(180, Math.floor(total * 0.5)));
      } catch {}
    }

    readDrawerHeight();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', readDrawerHeight);
    }

    let ro = null;
    try {
      const sidebarEl = rootRef.current ? rootRef.current.closest('.coms-sidebar') : null;
      if (sidebarEl && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => readDrawerHeight());
        ro.observe(sidebarEl);
      }
    } catch {}

    return () => {
      try {
        if (typeof window !== 'undefined') window.removeEventListener('resize', readDrawerHeight);
      } catch {}
      try {
        if (ro) ro.disconnect();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!topDrawerOpen) return undefined;

    function onKey(e) {
      if (e && e.key === 'Escape') {
        closeMenu();
        setTopDrawerOpen(false);
      }
    }

    function onDown(e) {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      try {
        if (e && e.target && rootEl.contains(e.target)) return;
      } catch {}
      closeMenu();
      setTopDrawerOpen(false);
    }

    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('touchstart', onDown, true);

    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('touchstart', onDown, true);
    };
  }, [topDrawerOpen, closeMenu]);

  useEffect(() => {
    if (!topDrawerOpen) return;
    closeMenu();
  }, [topDrawerOpen, closeMenu]);

  const topBannerStyle = useMemo(
    () => ({
      position: 'relative',
      height: topBannerHeight,
      minHeight: topBannerHeight,
      flexShrink: 0,
      background: 'var(--vxs-0, #06090f)',
      borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
      boxSizing: 'border-box',
      cursor: 'pointer',
      zIndex: 20,
      overflow: 'visible',
    }),
    [topBannerHeight]
  );

  const topDrawerStyle = useMemo(
    () => ({
      position: 'absolute',
      top: '100%',
      left: 0,
      width: '100%',
      height: topDrawerOpen ? topDrawerHeight : 0,
      overflow: 'hidden',
      background: 'var(--vxs-0, #06090f)',
      borderBottom: topDrawerOpen ? '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))' : '1px solid transparent',
      boxShadow: topDrawerOpen ? '0 14px 28px rgba(0,0,0,0.28)' : 'none',
      transition: 'height 180ms ease, border-bottom-color 180ms ease, box-shadow 180ms ease',
      pointerEvents: topDrawerOpen ? 'auto' : 'none',
    }),
    [topDrawerOpen, topDrawerHeight]
  );

  const actionRowStyle = useMemo(
    () => ({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: '12px 12px 10px',
      boxSizing: 'border-box',
      flexWrap: 'wrap',
      flexShrink: 0,
    }),
    []
  );

  const dividerStyle = useMemo(
    () => ({
      width: 'calc(100% - 24px)',
      height: 1,
      margin: '0 12px 10px',
      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
      borderRadius: 999,
      flexShrink: 0,
    }),
    []
  );

  const drawerGridStyle = useMemo(
    () => ({
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      gap: 12,
      padding: '0 12px 12px',
      boxSizing: 'border-box',
      overflowY: 'auto',
      minHeight: 0,
      flex: 1,
    }),
    []
  );

  const circleBtnStyle = useMemo(
    () => ({
      width: 52,
      height: 52,
      minWidth: 52,
      minHeight: 52,
      maxWidth: 52,
      maxHeight: 52,
      borderRadius: '50%',
      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
      background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flex: '0 0 52px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
      padding: 0,
      color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.95 * var(--vx-ov, 1)))',
      fontWeight: 800,
      fontSize: 14,
      lineHeight: 1,
    }),
    []
  );

  const anyUnread = nav.anyUnread;
  const anyMentions = nav.totalMentionCount > 0;
  const topBadgeText = formatMentionBadge(nav.totalMentionCount);

  function selectServer(sid) {
    setTopDrawerOpen(false);
    nav.selectServer(sid);
  }

  function goPointAtlas() {
    closeMenu();
    setTopDrawerOpen(false);
    if (typeof window !== 'undefined') {
      try {
        window.location.assign('https://point-atlas.io/');
      } catch {}
    }
  }

  function goVoxemera() {
    closeMenu();
    setTopDrawerOpen(false);
    requestManualVoxemeraLanding();
    if (typeof window !== 'undefined') {
      try {
        window.location.assign('/');
      } catch {}
    }
  }

  function createServer() {
    closeMenu();
    setTopDrawerOpen(false);
    if (typeof onCreateServer === 'function') {
      onCreateServer();
    }
  }

  return (
    <div ref={rootRef} style={topBannerStyle}>
      <button
        type="button"
        aria-label={topDrawerOpen ? 'Close server drawer' : 'Open server drawer'}
        onClick={() => {
          closeMenu();
          setTopDrawerOpen((v) => !v);
        }}
        style={{
          width: '100%',
          height: '100%',
          border: 0,
          padding: 0,
          margin: 0,
          background: 'transparent',
          color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
          display: 'block',
          textAlign: 'left',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 14px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <LogoFill url={voxLogoUrl} alt="" size={16} />
          </div>

          <div
            style={{
              position: 'absolute',
              right: 14,
              top: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            {anyMentions ? (
              <div
                aria-label={`Mentions: ${topBadgeText}`}
                title={`Mentions: ${topBadgeText}`}
                style={{
                  minWidth: 18,
                  height: 18,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: '#ff4d4f',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {topBadgeText}
              </div>
            ) : anyUnread ? (
              <div
                aria-label="Unread"
                title="Unread"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#ff4d4f',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                  flexShrink: 0,
                }}
              />
            ) : null}

            <div
              aria-hidden="true"
              style={{
                width: 9,
                height: 9,
                borderRight: '2px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.72 * var(--vx-ov, 1)))',
                borderBottom: '2px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.72 * var(--vx-ov, 1)))',
                transform: topDrawerOpen ? 'rotate(225deg)' : 'rotate(45deg)',
                transition: 'transform 160ms ease',
                marginTop: topDrawerOpen ? 5 : -2,
              }}
            />
          </div>
        </div>
      </button>

      <div style={topDrawerStyle}>
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={actionRowStyle}>
            <button
              type="button"
              title="Point Atlas"
              onClick={goPointAtlas}
              style={circleBtnStyle}
            >
              <LogoFill url={pointAtlasLogoUrl} alt="Point Atlas" size={24} />
            </button>

            <button
              type="button"
              title="Voxemera"
              onClick={goVoxemera}
              style={circleBtnStyle}
            >
              <LogoFill url={voxLogoUrl} alt="Voxemera" size={24} />
            </button>
          </div>

          <div style={dividerStyle} />

          <div style={drawerGridStyle}>
            {nav.servers.map((srv) => {
              const sid = String(srv && srv.id != null ? srv.id : '').trim();
              if (!sid) return null;

              const rawLogoUrl = nav.getServerLogo(srv);
              const hasDrawerLogo = !!rawLogoUrl;
              const isActiveServer = String(activeServerId || '') === sid;
              const badge = nav.getServerBadge(srv);
              const showUnread = badge.showUnread;
              const showMention = badge.showMention;
              const mentionBadgeText = badge.mentionText;

              return (
                <button
                  key={sid}
                  type="button"
                  title={srv && srv.name ? String(srv.name) : 'Server'}
                  onClick={() => selectServer(sid)}
                  onContextMenu={(e) => nav.openServerContextMenu(e, srv)}
                  style={{
                    ...circleBtnStyle,
                    border: isActiveServer ? '2px solid rgba(var(--vx-accent-rgb, 110,125,255), 0.95)' : circleBtnStyle.border,
                    boxShadow: isActiveServer ? '0 0 0 3px rgba(var(--vx-accent-rgb, 110,125,255), 0.18)' : circleBtnStyle.boxShadow,
                  }}
                >
                  {showMention ? (
                    <div
                      aria-label={`Mentions: ${mentionBadgeText}`}
                      title={`Mentions: ${mentionBadgeText}`}
                      style={{
                        position: 'absolute',
                        top: 3,
                        right: 3,
                        minWidth: 18,
                        height: 18,
                        padding: '0 5px',
                        borderRadius: 999,
                        background: '#ff4d4f',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        zIndex: 3,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 800,
                        lineHeight: 1,
                      }}
                    >
                      {mentionBadgeText}
                    </div>
                  ) : showUnread ? (
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#ff4d4f',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        zIndex: 3,
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}

                  {hasDrawerLogo ? (
                    <LogoFill
                      url={rawLogoUrl}
                      alt={srv && srv.name ? String(srv.name) : 'Server'}
                      onBroken={() => nav.markLogoBroken(sid)}
                    />
                  ) : (
                    <span>{String((srv && srv.acronym) || '?').slice(0, 3).toUpperCase()}</span>
                  )}
                </button>
              );
            })}

            <button
              type="button"
              title="Create server"
              onClick={createServer}
              style={{
                ...circleBtnStyle,
                borderStyle: 'dashed',
                fontSize: 24,
                fontWeight: 500,
              }}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <ServerContextMenu
        nav={nav}
        activeServerId={activeServerId}
        onOpenServerSettings={onOpenServerSettings}
      />
    </div>
  );
}